import { HintBox } from "./HintBox";
import type { PlanInteractionHintProps } from "./types";

const hints = {
  idleSetPosition: {
    key: "smartSurveyor.creationMode.hint.idleSetPosition",
  },
  idleAddElement: {
    key: "smartSurveyor.creationMode.hint.idleAddElement",
  },
  shiftAction: {
    key: "smartSurveyor.creationMode.hint.shiftAction",
  },
  editModeAction: {
    key: "smartSurveyor.creationMode.hint.editModeAction",
  },
  editModeResize: {
    key: "smartSurveyor.creationMode.hint.editModeResize",
  },
  editModeCancel: {
    key: "smartSurveyor.creationMode.hint.editModeCancel",
  },
  pathEditingDrag: {
    key: "smartSurveyor.creationMode.hint.pathEditingDrag",
  },
  pathEditingInsert: {
    key: "smartSurveyor.creationMode.hint.pathEditingInsert",
  },
  pathEditingDelete: {
    key: "smartSurveyor.creationMode.hint.pathEditingDelete",
  },
  routeDrawingAddNode: {
    key: "smartSurveyor.creationMode.hint.routeDrawingAddNode",
  },
  routeDrawingFinish: {
    key: "smartSurveyor.creationMode.hint.routeDrawingFinish",
  },
  areaDrawingAddPoint: {
    key: "smartSurveyor.creationMode.hint.areaDrawingAddPoint",
  },
  areaDrawingFinish: {
    key: "smartSurveyor.creationMode.hint.areaDrawingFinish",
  },
  roomDrawingFinish: {
    key: "smartSurveyor.creationMode.hint.roomDrawingFinish",
  },
} as const;

export const PlanInteractionHint = ({
  isHoveringPlan,
  interactionMode,
  isRouteDrawingActive,
  isPolygonEditingActive,
  creationModifier,
}: PlanInteractionHintProps) => {
  // Only idle creating-element (no modifier) skips the hover highlight.
  const isHighlighted =
    isHoveringPlan &&
    !(
      interactionMode.kind === "creating-element" && creationModifier === "none"
    );

  if (interactionMode.kind === "drawing-route") {
    if (!isRouteDrawingActive) return null;
    return (
      <HintBox
        entries={[hints.routeDrawingAddNode, hints.routeDrawingFinish]}
        isHighlighted={isHighlighted}
      />
    );
  }

  if (
    interactionMode.kind === "selection-areas" ||
    interactionMode.kind === "drawing-rooms" ||
    interactionMode.kind === "editing-room"
  ) {
    if (
      interactionMode.kind !== "editing-room" &&
      interactionMode.step === "drawing"
    ) {
      const finishHint =
        interactionMode.kind === "drawing-rooms"
          ? hints.roomDrawingFinish
          : hints.areaDrawingFinish;
      return (
        <HintBox
          entries={[hints.areaDrawingAddPoint, finishHint]}
          isHighlighted={isHighlighted}
        />
      );
    }
    if (isPolygonEditingActive) {
      return (
        <HintBox
          entries={[
            hints.pathEditingDrag,
            hints.pathEditingInsert,
            hints.pathEditingDelete,
          ]}
          isHighlighted={isHighlighted}
        />
      );
    }
    return null;
  }

  if (interactionMode.kind === "editing-path") {
    return (
      <HintBox
        entries={[
          hints.pathEditingDrag,
          hints.pathEditingInsert,
          hints.pathEditingDelete,
        ]}
        isHighlighted={isHighlighted}
      />
    );
  }

  if (interactionMode.kind === "editing-element") {
    return (
      <HintBox
        entries={[
          hints.editModeAction,
          hints.editModeResize,
          hints.editModeCancel,
        ]}
        isHighlighted={isHighlighted}
      />
    );
  }

  if (interactionMode.kind === "creating-element") {
    if (creationModifier === "ctrl") {
      return (
        <HintBox
          entries={[
            hints.editModeAction,
            hints.editModeResize,
            hints.editModeCancel,
          ]}
          isHighlighted={isHighlighted}
        />
      );
    }
    // Shift/idle hints only apply during element placement (adding-elements step).
    if (interactionMode.step !== "adding-elements") return null;
    if (creationModifier === "shift") {
      return (
        <HintBox
          entries={[hints.shiftAction, hints.editModeCancel]}
          isHighlighted={isHighlighted}
        />
      );
    }
    return (
      <HintBox
        entries={[hints.idleSetPosition, hints.idleAddElement]}
        isHighlighted={isHighlighted}
      />
    );
  }

  return null;
};
